import { Calendar, ChevronRight, CircleAlert } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { useOverlayLinkState } from '../attendee-preview/attendee-detail-dialog';
import { initialsOf, roleAtCompany } from '../attendee-preview/constants';
import { SaveToMeetButton } from '../attendee-preview/save-to-meet-panel';
import { ConnectButton } from '../connections/connect-action-panel';
import { prefilledMatchMessage } from '../connections/constants';

import { WHY_YOU_SHOULD_MEET_LABEL } from './constants';
import type { MatchSet, MatchSuggestion } from './types';

/**
 * The match card (`MEM-025` AC-3).
 *
 * ## The order the AC fixes, top to bottom
 *
 * 1. the event chip — **cross-event view only**
 * 2. the avatar or initials, name, role and company, **as one selectable region opening the
 *    detail**
 * 3. the narrative
 * 4. the actions
 *
 * The JSX is in that order, and `apps/web/__tests__/matching/matches-screens.test.ts` asserts it
 * by source position, because that is the only kind of render assertion this repository can make.
 *
 * ## Transcribed from `Member App.dc.html`, the matches screen
 *
 * A 2px `--spark-gradient` border wrapper at `--radius-xl` with `--shadow-md`, around a
 * `--surface-card` panel at `22px` radius and 20px padding. The person row is a 56px `999px`
 * initials tile at `font-brand 700/18px` with a `0 0 0 2px --fuchsia-200` ring, the name as an
 * `<h3>` at `var(--text-lg)` truncated to one line, and the role beneath at `var(--text-sm)` in
 * `--text-muted`. The narrative sits in a `--purple-50` block at `--radius-md`, under a
 * `font-brand 600/var(--text-2xs)` uppercase `.08em` label in `--brand-primary` reading **Why you
 * should meet**. The actions are two pill buttons in a 10px row.
 *
 * ## What the prototype carries that this does not
 *
 * **`match: "92%"`.** Every fixture match in the design source has one and the card's view model
 * passes it through — and no template renders it. It is not in the API payload and it is not
 * here. It is the single most likely thing to be re-added by somebody reading the design data
 * rather than the design's markup, which is why it is written down in three places.
 *
 * ## Connect, added 2026-08-23 with `MEM-032`
 *
 * `MEM-025` AC-7 names it and `MEM-034` AC-5 fixes the set: **the only actions offered on a match
 * are Connect and Save to Meet.** Both are here now and nothing else is — no message control, no
 * follow control, no dismiss. `no-member-to-member-message-surface.test.ts` scans this file for a
 * third action and fails if one appears.
 *
 * Connect opens `connections`' composer (`MEM-032` AC-3), pre-filled from the match context
 * (AC-4). The dialog lives in the connections slice rather than here so that a match card, an
 * attendee profile and the in-event roster open **one** composer — the same argument AC-7 makes
 * about Save to Meet writing one list.
 */
export function MatchCard({
  suggestion,
  set,
  showEventChip,
}: {
  readonly suggestion: MatchSuggestion;
  readonly set: MatchSet;
  /** `MEM-025` AC-3: the chip is the cross-event view's, and only its. */
  readonly showEventChip: boolean;
}): React.ReactElement {
  const name =
    suggestion.lastName === null
      ? suggestion.firstName
      : `${suggestion.firstName} ${suggestion.lastName}`;
  const line = roleAtCompany(suggestion);
  const detailPath =
    suggestion.attendeeRef === null
      ? null
      : `/app/events/${encodeURIComponent(set.eventId)}/matches/${encodeURIComponent(suggestion.attendeeRef)}`;

  return (
    /* §3.8: a 2px `--spark-gradient` border at `--radius-xl` with `--shadow-md`, around a
       `--surface-card` panel at 22px radius and 22px padding. `h-full` so three cards in a row are
       the height of the tallest — a grid of cards that end at different heights is the thing that
       makes a 3-column layout read as a list that went wrong. */
    <article className="h-full rounded-xl bg-spark p-0.5 shadow-md">
      <div className="flex h-full flex-col gap-3.5 rounded-[22px] bg-card p-5 md:p-[22px]">
        {/* 1. The event chip. */}
        {showEventChip && set.eventName !== null ? (
          <p className="inline-flex items-center gap-1.5 rounded-pill bg-brand-50 px-2.5 py-1 font-brand text-2xs font-bold text-brand">
            <Calendar className="h-3 w-3" aria-hidden />
            {set.eventName}
          </p>
        ) : null}

        {/* 2. The person, as one selectable region. A `<Link>` rather than a `<div onClick>` so
            it is a real focus stop with a real accessible name (NFR accessibility#wcag-aa). A
            card whose person did not resolve has no profile to open and renders as plain text
            rather than as a link to nowhere. */}
        <PersonRegion name={name} line={line} suggestion={suggestion} to={detailPath} />

        {/* 3. The narrative. **The whole feature** — since no score is shown, this sentence is
            the only thing a member reads about why these two should meet. */}
        <div className="rounded-md bg-brand-50 px-3.5 py-3">
          <h4 className="mb-1 font-brand text-2xs font-semibold uppercase tracking-caps text-brand">
            {WHY_YOU_SHOULD_MEET_LABEL}
          </h4>
          <p className="text-sm leading-snug text-text-body">{suggestion.narrative}</p>
        </div>

        {/* `MEM-026` AC-3: a withdrawn card states it rather than vanishing. Above the actions,
            because it is the reason the actions are not offered.

            **And the same treatment when the person has left but the sweep has not run yet.** Both
            are the one state where a control genuinely cannot act, and the card would otherwise
            draw a name, a role, a narrative and then a gap where two controls belong.

            What this branch deliberately no longer covers is an **unlisted** match. For a day it
            did: a matched member with privacy election (a) off had no `attendeeRef`, so both
            controls returned `null`. Sumit's ruling of 2026-09-13 — *"if person opted in for
            matching and is matched then ok to show and act on all actions"* — made that card an
            ordinary one, and there is nothing left to say about it.

            `?? null` and not `!== null` alone: a payload without the field at all — a response
            cached before it existed, or served by an older instance mid-deploy — makes this
            `undefined`, and `undefined !== null` is true, which would hide both controls on a
            perfectly ordinary card. Found by a test whose fixture predated the field, which is
            exactly the shape the real thing would have taken. */}
        {suggestion.withdrawn || (suggestion.unreachableNotice ?? null) !== null ? (
          <p className="mt-auto flex items-center gap-1.5 rounded-md bg-muted p-3 text-xs text-muted-foreground">
            <CircleAlert className="h-4 w-4 shrink-0" aria-hidden />
            {suggestion.withdrawn ? suggestion.withdrawnNotice : suggestion.unreachableNotice}
          </p>
        ) : (
          /* 4. The actions — `MEM-034` AC-5's complete set, and nothing beside them. §3.8 stacks
              them in a 9px column, Save over Connect, each full width; `mt-auto` holds them at the
              foot of a card whose narrative is shorter than its neighbour's. */
          <div className="mt-auto flex flex-col gap-[9px]">
            <SaveToMeetButton
              eventId={set.eventId}
              attendeeRef={suggestion.attendeeRef}
              saved={suggestion.savedToMeetList}
              variant="match"
            />
            <ConnectButton
              eventId={set.eventId}
              attendeeRef={suggestion.attendeeRef}
              person={suggestion}
              prefilledMessage={prefilledMatchMessage({
                firstName: suggestion.firstName,
                eventName: set.eventName,
              })}
              relationship={suggestion.relationship}
              connectionRef={suggestion.connectionRef}
              variant="match"
            />
          </div>
        )}
      </div>
    </article>
  );
}

function PersonRegion({
  name,
  line,
  suggestion,
  to,
}: {
  readonly name: string;
  readonly line: string | null;
  readonly suggestion: MatchSuggestion;
  readonly to: string | null;
}): React.ReactElement {
  /* §2.16: the match card's head opens the person over the Matches page. */
  const overlayState = useOverlayLinkState();
  const inner = (
    <>
      <span
        className="inline-flex h-[54px] w-[54px] shrink-0 items-center justify-center rounded-pill bg-brand-200 font-brand text-[17px] font-bold text-brand-700"
        aria-hidden
      >
        {initialsOf({ firstName: suggestion.firstName, lastName: suggestion.lastName ?? '' })}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-md font-semibold text-foreground">{name}</span>
        {line === null ? null : (
          <span className="mt-0.5 block text-sm text-muted-foreground">{line}</span>
        )}
      </span>
    </>
  );

  if (to === null) {
    return <div className="flex items-center gap-[13px]">{inner}</div>;
  }
  return (
    <Link to={to} state={overlayState} className="flex items-center gap-[13px]">
      {inner}
      <ChevronRight className="h-[17px] w-[17px] shrink-0 text-text-subtle" aria-hidden />
    </Link>
  );
}
